/**
 * EntityHistory - Historique complet d'une entite
 *
 * Affiche l'historique des consultations et modifications d'une entite:
 * - Qui a consulte
 * - Qui a cree/modifie/supprime
 * - Quand et quels changements
 *
 * Reutilisable pour tous types d'entites:
 * - prestation, user, client, group, location, expense, leave_request
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import ActivityIcon from './ActivityIcon';
import ActivityDiff from './ActivityDiff';

const EntityHistory = ({
  entityType,
  entityId,
  token,
  limit = 30,
  showHeader = true,
  title = null,
  compact = false
}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!entityType || !entityId || !token) return;

    setLoading(true);
    setError(null);

    try {
      const url = `${API_ENDPOINTS.BASE_URL}/api/analytics/dashboard/entity-history/${entityType}/${entityId}?limit=${limit}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Acces non autorise');
        }
        throw new Error('Erreur lors du chargement');
      }

      const result = await response.json();

      if (result.success) {
        setHistory(result.data.history || []);
      } else {
        throw new Error(result.error || 'Erreur inconnue');
      }
    } catch (err) {
      console.error('Erreur chargement historique:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, token, limit]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEntityTypeLabel = (type) => {
    const labels = {
      prestation: 'Prestation',
      user: 'Utilisateur',
      client: 'Client',
      group: 'Groupe',
      location: 'Lieu',
      expense: 'Note de frais',
      leave_request: 'Demande de conges'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div style={{ padding: compact ? '20px' : '40px', textAlign: 'center', color: '#666' }}>
        Chargement...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '16px',
        background: '#ffebee',
        borderRadius: '8px',
        color: '#c62828',
        fontSize: '13px'
      }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: compact ? '8px' : '12px',
      overflow: 'hidden',
      boxShadow: compact ? 'none' : '0 2px 10px rgba(0,0,0,0.08)',
      border: compact ? '1px solid #e0e0e0' : 'none'
    }}>
      {showHeader && (
        <div style={{
          padding: compact ? '12px 16px' : '16px 20px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8f9fa'
        }}>
          <h4 style={{ margin: 0, fontSize: compact ? '14px' : '16px', fontWeight: 600 }}>
            📜 {title || `Historique ${getEntityTypeLabel(entityType)}`}
          </h4>
          <span style={{ fontSize: '12px', color: '#666' }}>
            {history.length} entree{history.length > 1 ? 's' : ''}
          </span>
        </div>
      )}

      <div style={{ padding: compact ? '12px' : '16px 20px' }}>
        {history.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: compact ? '20px' : '40px',
            color: '#666',
            fontSize: '13px'
          }}>
            Aucun historique disponible
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: compact ? '8px' : '12px'
          }}>
            {history.map((event, idx) => (
              <div
                key={event.id || idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: compact ? '8px' : '12px',
                  background: idx === 0 ? '#e3f2fd' : '#fafafa',
                  borderRadius: '8px',
                  borderLeft: idx === 0 ? '3px solid #1976d2' : '3px solid transparent'
                }}
              >
                <ActivityIcon type={event.type} size={compact ? 'small' : 'medium'} />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: compact ? '13px' : '14px',
                    fontWeight: 500,
                    color: '#333',
                    marginBottom: '2px'
                  }}>
                    {event.label}
                  </div>

                  <div style={{
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    {formatTimestamp(event.timestamp)}
                    {event.userName && (
                      <span style={{ marginLeft: '8px', color: '#1976d2' }}>
                        par {event.userName}
                      </span>
                    )}
                  </div>

                  {!compact && event.details && (
                    <ActivityDiff details={event.details} collapsed={true} />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityHistory;
